import * as THREE from 'three';
import { ROOM } from './world';
import { menuBoardTexture, steamTexture, kutaniTexture } from './textures';

const wood = (color, roughness = 0.75) => new THREE.MeshStandardMaterial({ color, roughness });
const metal = (color, roughness = 0.35) =>
  new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.85 });

function mesh(geo, mat, x, y, z, parent) {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  parent.add(m);
  return m;
}

// ── エスプレッソマシン（カウンター上）──
function addEspressoMachine(scene) {
  const g = new THREE.Group();
  const body = metal(0x8a8f94, 0.3);
  const dark = new THREE.MeshStandardMaterial({ color: 0x23262a, roughness: 0.5, metalness: 0.4 });

  mesh(new THREE.BoxGeometry(0.62, 0.34, 0.4), body, 0, 0.17, 0, g);
  mesh(new THREE.BoxGeometry(0.66, 0.05, 0.44), dark, 0, 0.37, 0, g); // 天板
  mesh(new THREE.BoxGeometry(0.66, 0.05, 0.44), dark, 0, 0.025, 0, g); // 台座
  // グループヘッド2つ＋ポルタフィルター
  [-0.15, 0.15].forEach((x) => {
    mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.08, 12), dark, x, 0.1, 0.17, g);
    mesh(new THREE.CylinderGeometry(0.05, 0.04, 0.03, 12), metal(0xb8bcc0), x, 0.05, 0.17, g);
    const handle = mesh(new THREE.CylinderGeometry(0.012, 0.015, 0.11, 8), dark, x, 0.035, 0.26, g);
    handle.rotation.x = Math.PI / 2;
  });
  // スチームワンド
  const wand = mesh(new THREE.CylinderGeometry(0.008, 0.008, 0.2, 8), metal(0xb8bcc0), 0.34, 0.12, 0.1, g);
  wand.rotation.z = 0.5;
  // ゲージ
  const gauge = mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.015, 16), new THREE.MeshStandardMaterial({ color: 0xf5f2eb, roughness: 0.3 }), 0, 0.24, 0.205, g);
  gauge.rotation.x = Math.PI / 2;

  g.position.set(-2.6, 1.11, -3.25);
  scene.add(g);
}

// ── コーヒーグラインダー ──
function addGrinder(scene) {
  const g = new THREE.Group();
  const body = new THREE.MeshStandardMaterial({ color: 0x2b2f26, roughness: 0.45, metalness: 0.3 });
  mesh(new THREE.CylinderGeometry(0.07, 0.09, 0.3, 16), body, 0, 0.15, 0, g);
  // ホッパー（豆入れ）
  // transmissionはモバイルGPUに重いので、軽い半透明で疑似ガラスにする
  const hopper = new THREE.MeshStandardMaterial({
    color: 0x8a7050,
    roughness: 0.1,
    transparent: true,
    opacity: 0.5,
  });
  mesh(new THREE.CylinderGeometry(0.075, 0.055, 0.14, 16), hopper, 0, 0.37, 0, g);
  mesh(new THREE.CylinderGeometry(0.078, 0.078, 0.015, 16), body, 0, 0.445, 0, g);
  g.position.set(-3.4, 1.11, -3.3);
  scene.add(g);
}

// ── ハンドドリップスタンド＋ケトル ──
function addBrewBar(scene) {
  const g = new THREE.Group();
  const brass = metal(0xb89d5a, 0.4);
  const dark = wood(0x2e241a);

  // ドリップスタンド
  mesh(new THREE.BoxGeometry(0.3, 0.02, 0.14), dark, 0, 0.01, 0, g);
  mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.26, 8), brass, -0.1, 0.14, 0, g);
  mesh(new THREE.BoxGeometry(0.16, 0.015, 0.14), dark, 0, 0.27, 0, g);
  // ドリッパー＋サーバー
  mesh(new THREE.CylinderGeometry(0.055, 0.03, 0.07, 12), new THREE.MeshStandardMaterial({ color: 0xf5f2eb, roughness: 0.4 }), 0.02, 0.315, 0, g);
  mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.12, 12), new THREE.MeshStandardMaterial({ color: 0xcfc8b8, roughness: 0.05, transparent: true, opacity: 0.35 }), 0.02, 0.08, 0, g);

  // 細口ケトル
  const kettle = new THREE.Group();
  mesh(new THREE.CylinderGeometry(0.07, 0.085, 0.13, 16), metal(0x707478, 0.25), 0, 0.065, 0, kettle);
  mesh(new THREE.SphereGeometry(0.07, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), metal(0x707478, 0.25), 0, 0.13, 0, kettle);
  const spout = mesh(new THREE.CylinderGeometry(0.01, 0.016, 0.16, 8), metal(0x707478, 0.25), 0.1, 0.1, 0, kettle);
  spout.rotation.z = -1.0;
  kettle.position.set(0.28, 0, 0.02);
  g.add(kettle);

  g.position.set(-0.4, 1.11, -3.3);
  scene.add(g);
}

// ── 黒板メニュー（奥壁）──
function addMenuBoard(scene) {
  const { depth: D } = ROOM;
  const g = new THREE.Group();
  const frame = mesh(
    new THREE.BoxGeometry(1.7, 1.3, 0.05),
    wood(0x3a2e20),
    0, 0, 0, g
  );
  frame.castShadow = false;
  const board = mesh(
    new THREE.PlaneGeometry(1.56, 1.16),
    new THREE.MeshStandardMaterial({ map: menuBoardTexture(), roughness: 0.9 }),
    0, 0, 0.03, g
  );
  board.castShadow = false;
  g.position.set(3.1, 2.05, -D / 2 + 0.08);
  scene.add(g);
}

// ── 観葉植物 ──
function addPlant(scene, x, z, scale = 1, colliders = null) {
  const g = new THREE.Group();
  mesh(new THREE.CylinderGeometry(0.16, 0.12, 0.26, 12), new THREE.MeshStandardMaterial({ color: 0x9a5f3c, roughness: 0.9 }), 0, 0.13, 0, g);
  mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.03, 12), new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 1 }), 0, 0.27, 0, g);
  const leaf = new THREE.MeshStandardMaterial({ color: 0x2e6b45, roughness: 0.85 });
  const leafDark = new THREE.MeshStandardMaterial({ color: 0x1f4d32, roughness: 0.85 });
  mesh(new THREE.CylinderGeometry(0.015, 0.02, 0.5, 6), leafDark, 0, 0.5, 0, g);
  [[0.16, 0.62, 0.05], [-0.14, 0.7, -0.08], [0.02, 0.82, 0.12], [-0.05, 0.9, -0.05], [0.1, 0.95, -0.1]].forEach(([lx, ly, lz], i) => {
    const l = mesh(new THREE.SphereGeometry(0.14 + (i % 2) * 0.05, 8, 6), i % 2 ? leaf : leafDark, lx, ly, lz, g);
    l.scale.y = 0.55;
  });
  g.scale.setScalar(scale);
  g.position.set(x, 0, z);
  scene.add(g);
  if (colliders) {
    const r = 0.18 * scale;
    colliders.push(
      new THREE.Box3(new THREE.Vector3(x - r, 0, z - r), new THREE.Vector3(x + r, 1.0 * scale, z + r))
    );
  }
}

// ── 九谷焼コラボの展示テーブル ──
function addKutaniCorner(scene, colliders) {
  const g = new THREE.Group();
  // 丸テーブル
  mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.04, 24), wood(0x4e3a28), 0, 0.82, 0, g);
  mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.8, 10), wood(0x2e241a), 0, 0.41, 0, g);
  mesh(new THREE.CylinderGeometry(0.24, 0.28, 0.03, 16), wood(0x2e241a), 0, 0.015, 0, g);

  // 九谷焼カップ3つ（五彩の絵付け）
  [[-0.2, 0, 1], [0.13, 0.12, 2], [0.08, -0.2, 3]].forEach(([x, z, seed]) => {
    const cup = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.038, 0.08, 16, 1, true),
      new THREE.MeshStandardMaterial({ map: kutaniTexture(seed), roughness: 0.25, side: THREE.DoubleSide })
    );
    cup.position.set(x, 0.88, z);
    cup.castShadow = true;
    g.add(cup);
    // 中身の白
    const inner = new THREE.Mesh(
      new THREE.CylinderGeometry(0.048, 0.048, 0.005, 16),
      new THREE.MeshStandardMaterial({ color: 0xf6f1e6, roughness: 0.3 })
    );
    inner.position.set(x, 0.9, z);
    g.add(inner);
    // ソーサー
    const saucer = new THREE.Mesh(
      new THREE.CylinderGeometry(0.075, 0.06, 0.012, 16),
      new THREE.MeshStandardMaterial({ color: 0xf6f1e6, roughness: 0.3 })
    );
    saucer.position.set(x, 0.845, z);
    saucer.castShadow = true;
    g.add(saucer);
  });

  g.position.set(4.4, 0, 3.0);
  scene.add(g);
  colliders.push(
    new THREE.Box3(new THREE.Vector3(3.95, 0, 2.55), new THREE.Vector3(4.85, 1.0, 3.45))
  );
}

// ── 棚の小物（豆の瓶・スケール）──
function addShelfProps(scene) {
  const { width: W } = ROOM;
  const shelfX = W / 2 - 0.45;
  const glass = new THREE.MeshStandardMaterial({
    color: 0xd8d2c2,
    roughness: 0.05,
    transparent: true,
    opacity: 0.35,
  });
  const beans = new THREE.MeshStandardMaterial({ color: 0x4a2f1c, roughness: 0.95 });
  const lid = wood(0x2e241a);

  // 上段: 豆の保存瓶
  [-1.6, -1.0, 0.9, 1.5].forEach((z, i) => {
    const h = 0.22 + (i % 2) * 0.06;
    const jar = new THREE.Group();
    mesh(new THREE.CylinderGeometry(0.07, 0.07, h, 12), glass, 0, h / 2, 0, jar);
    mesh(new THREE.CylinderGeometry(0.065, 0.065, h * 0.55, 12), beans, 0, h * 0.3, 0, jar);
    mesh(new THREE.CylinderGeometry(0.072, 0.072, 0.025, 12), lid, 0, h + 0.012, 0, jar);
    jar.position.set(shelfX, 1.925, z);
    scene.add(jar);
  });

  // 下段: ドリップスケールと麻袋
  const scaleBody = mesh(new THREE.BoxGeometry(0.16, 0.03, 0.13), new THREE.MeshStandardMaterial({ color: 0x23262a, roughness: 0.4 }), shelfX, 0.84, -1.4, scene);
  scaleBody.castShadow = false;
  const sack = mesh(
    new THREE.SphereGeometry(0.16, 10, 8),
    new THREE.MeshStandardMaterial({ color: 0xa08a62, roughness: 1 }),
    shelfX, 0.91, 1.1, scene
  );
  sack.scale.set(1, 0.75, 1);
}

// ── テーブルの上のカップ＆湯気 ──
function addTableCoffee(scene, steamSprites) {
  const steamTex = steamTexture();
  [
    [-3.5, 1.8],
    [1.5, 2.2],
  ].forEach(([x, z], ti) => {
    const cx = x + (ti ? -0.18 : 0.15);
    const cz = z + 0.1;
    // カップ＋ソーサー＋コーヒー表面
    const cupMat = new THREE.MeshStandardMaterial({ color: 0xf5f2eb, roughness: 0.35 });
    const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.034, 0.06, 16, 1, true), cupMat);
    cup.position.set(cx, 0.775, cz);
    cup.castShadow = true;
    scene.add(cup);
    const saucer = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.055, 0.01, 16), cupMat);
    saucer.position.set(cx, 0.75, cz);
    scene.add(saucer);
    const coffee = new THREE.Mesh(
      new THREE.CylinderGeometry(0.042, 0.042, 0.004, 16),
      new THREE.MeshStandardMaterial({ color: 0x2b1608, roughness: 0.2 })
    );
    coffee.position.set(cx, 0.8, cz);
    scene.add(coffee);

    // 湯気（スプライト3枚を位相ずらしで上昇ループ）
    for (let i = 0; i < 3; i++) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: steamTex, transparent: true, opacity: 0, depthWrite: false })
      );
      sprite.scale.setScalar(0.09);
      sprite.position.set(cx, 0.84, cz);
      scene.add(sprite);
      steamSprites.push({ sprite, baseY: 0.84, x: cx, z: cz, phase: (i / 3) * Math.PI * 2 });
    }
  });
}

// ── 日差しの中の塵（パーティクル）──
function addDustMotes(scene) {
  const count = 110;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // 窓（左壁）からの光の帯あたりに散らす
    positions[i * 3] = -5.6 + Math.random() * 4.2;
    positions[i * 3 + 1] = 0.4 + Math.random() * 2.2;
    positions[i * 3 + 2] = -1.2 + Math.random() * 3.6;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const points = new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      color: 0xffe8c4,
      size: 0.014,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
  scene.add(points);
  return points;
}

/**
 * 店内の小物・空気感を追加する。
 * 返り値: { update(elapsed) } — 湯気・塵のアニメーション
 */
export function addDecor(scene, colliders) {
  const steamSprites = [];

  addEspressoMachine(scene);
  addGrinder(scene);
  addBrewBar(scene);
  addMenuBoard(scene);
  addShelfProps(scene);
  addTableCoffee(scene, steamSprites);
  addKutaniCorner(scene, colliders);
  addPlant(scene, -5.3, -3.7, 1.75, colliders); // 窓ぎわ（人の身長ほど）
  addPlant(scene, 5.3, -3.9, 1.6, colliders); // 棚の奥
  addPlant(scene, -5.35, 3.8, 1.55, colliders); // 入口よこ
  const dust = addDustMotes(scene);

  const update = (elapsed) => {
    // 湯気: ゆっくり上昇→フェードアウトのループ
    for (const s of steamSprites) {
      const t = ((elapsed * 0.5 + s.phase) % (Math.PI * 2)) / (Math.PI * 2);
      s.sprite.position.set(
        s.x + Math.sin(elapsed * 1.7 + s.phase) * 0.015,
        s.baseY + t * 0.28,
        s.z
      );
      s.sprite.material.opacity = 0.5 * Math.sin(t * Math.PI);
      s.sprite.scale.setScalar(0.07 + t * 0.09);
    }
    // 塵: ゆらゆら漂う
    dust.rotation.y = Math.sin(elapsed * 0.05) * 0.08;
    dust.position.y = Math.sin(elapsed * 0.18) * 0.05;
  };

  return { update };
}
